import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const KitchenDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, preparing, ready
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchOrders();
    
    // Auto-refresh every 10 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchOrders, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchOrders = async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('restaurant_orders')
        .select(`
          *,
          order_items (
            *,
            menu_item:menu_items (
              name,
              preparation_time
            )
          )
        `)
        .in('status', ['pending', 'confirmed', 'preparing', 'ready'])
        .order('created_at', { ascending: true });

      if (ordersError) throw ordersError;

      setOrders(ordersData || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const { error } = await supabase
        .from('restaurant_orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      // Create notification
      let notificationMessage = '';
      switch(newStatus) {
        case 'confirmed':
          notificationMessage = 'Your order has been confirmed and will be prepared shortly';
          break;
        case 'preparing':
          notificationMessage = 'Your order is now being prepared';
          break;
        case 'ready':
          notificationMessage = 'Your order is ready!';
          break;
        case 'served':
          notificationMessage = 'Your order has been served. Enjoy your meal!';
          break;
        default:
          notificationMessage = '';
          break;
      }

      if (notificationMessage) {
        await supabase
          .from('order_notifications')
          .insert([{
            order_id: orderId,
            sender_type: 'kitchen',
            message: notificationMessage,
            is_read: false
          }]);
      }

      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const updateItemStatus = async (itemId, newStatus) => {
    try {
      const { error } = await supabase
        .from('order_items')
        .update({ status: newStatus })
        .eq('id', itemId);

      if (error) throw error;
      fetchOrders();
    } catch (error) {
      console.error('Error updating item status:', error);
    }
  };

  const sendNotification = async (orderId, message) => {
    try {
      const { error } = await supabase
        .from('order_notifications')
        .insert([{
          order_id: orderId,
          sender_type: 'kitchen',
          message: message,
          is_read: false
        }]);

      if (error) throw error;
      alert('Notification sent to customer');
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(order => order.status === filter);

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ff9800';
      case 'confirmed': return '#2196F3';
      case 'preparing': return '#9c27b0';
      case 'ready': return '#4CAF50';
      default: return '#666';
    }
  };

  const getOrderPriority = (order) => {
    const orderTime = new Date(order.created_at);
    const now = new Date();
    const minutesWaiting = Math.floor((now - orderTime) / 60000);
    
    if (minutesWaiting > 30) return 'urgent';
    if (minutesWaiting > 20) return 'high';
    return 'normal';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-wrapper">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper" style={{ maxWidth: '1400px' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '2rem',
          padding: '1.5rem',
          background: 'white',
          borderRadius: '8px',
          border: '2px solid var(--color-accent-gold)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div>
            <h1 style={{ color: 'var(--color-accent-gold)', marginBottom: '0.5rem', fontSize: '2rem' }}>Kitchen Dashboard</h1>
            <p style={{ color: 'var(--color-gray-dark)', fontSize: '0.95rem', opacity: 0.7 }}>
              {orders.length} Active Orders
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-gray-dark)', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            
            <button
              onClick={fetchOrders}
              className="btn btn-primary"
            >
              🔄 Refresh
            </button>

            <button
              onClick={() => navigate('/restaurant-admin')}
              className="btn btn-secondary"
            >
              Admin Dashboard
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={{ 
          display: 'inline-flex', 
          gap: '0.5rem',
          marginBottom: '2rem',
          background: 'white',
          padding: '0.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          {['all', 'pending', 'confirmed', 'preparing', 'ready'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={filter === status ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ 
                textTransform: 'capitalize',
                fontSize: '0.9rem',
                padding: '0.5rem 1rem',
                minWidth: 'auto',
                fontWeight: '600',
                margin: 0
              }}
            >
              {status} {status !== 'all' && `(${orders.filter(o => o.status === status).length})`}
            </button>
          ))}
        </div>

        {/* Orders Grid */}
        {filteredOrders.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <h3 style={{ color: 'var(--color-accent-gold)', marginBottom: '0.5rem' }}>No orders to display</h3>
            <p style={{ color: 'var(--color-gray-dark)', opacity: 0.6, marginTop: '0.5rem' }}>Orders will appear here automatically</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: '1rem' 
          }}>
            {filteredOrders.map(order => {
              const priority = getOrderPriority(order);
              const orderTime = new Date(order.created_at);
              const minutesWaiting = Math.floor((new Date() - orderTime) / 60000);
              
              return (
                <div 
                  key={order.id} 
                  className="card" 
                  style={{ 
                    padding: '1.5rem',
                    background: priority === 'urgent' ? '#fff5f5' : 
                               priority === 'high' ? '#fff8e1' : 
                               'white',
                    border: `2px solid ${getStatusColor(order.status)}`,
                    position: 'relative'
                  }}
                >
                  {/* Priority Badge */}
                  {priority !== 'normal' && (
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      padding: '0.25rem 0.5rem',
                      background: priority === 'urgent' ? 'red' : 'orange',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase'
                    }}>
                      {priority}
                    </div>
                  )}

                  {/* Order Header */}
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <h3 style={{ color: 'var(--color-accent-gold)', margin: 0, fontSize: '1.5rem' }}>
                        Table {order.table_number}
                      </h3>
                      <span style={{ 
                        padding: '0.25rem 0.75rem',
                        background: getStatusColor(order.status),
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {order.status}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, color: 'var(--color-gray-dark)' }}>
                      Order #{order.order_number}
                    </div>
                    
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, color: 'var(--color-gray-dark)' }}>
                      ⏱ {minutesWaiting} minutes ago
                    </div>
                  </div>

                  {/* Order Items */}
                  <div style={{ marginBottom: '1rem' }}>
                    {order.order_items.map(item => (
                      <div 
                        key={item.id} 
                        style={{ 
                          padding: '0.75rem',
                          background: '#f9f9f9',
                          borderRadius: '4px',
                          marginBottom: '0.5rem',
                          borderLeft: `4px solid ${getStatusColor(item.status)}`
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                          <strong>{item.quantity}x {item.menu_item?.name}</strong>
                          <span style={{ 
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            background: getStatusColor(item.status),
                            color: 'white',
                            borderRadius: '4px'
                          }}>
                            {item.status}
                          </span>
                        </div>
                        
                        {item.special_instructions && (
                          <div style={{ 
                            fontSize: '0.85rem', 
                            opacity: 0.9,
                            marginTop: '0.25rem',
                            fontStyle: 'italic',
                            color: '#d97706',
                            fontWeight: '500'
                          }}>
                            📝 {item.special_instructions}
                          </div>
                        )}

                        {/* Item Status Update */}
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                          {item.status !== 'preparing' && (
                            <button
                              onClick={() => updateItemStatus(item.id, 'preparing')}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#9c27b0',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              Start Preparing
                            </button>
                          )}
                          {item.status === 'preparing' && (
                            <button
                              onClick={() => updateItemStatus(item.id, 'ready')}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              Mark Ready
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Special Instructions */}
                  {order.special_instructions && (
                    <div style={{ 
                      padding: '0.75rem',
                      background: '#fff8e1',
                      borderRadius: '4px',
                      marginBottom: '1rem',
                      border: '1px solid #ffc107'
                    }}>
                      <strong style={{ color: '#d97706' }}>⚠️ Special Instructions:</strong>
                      <p style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: 'var(--color-gray-dark)' }}>
                        {order.special_instructions}
                      </p>
                    </div>
                  )}

                  {/* Order Total */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    padding: '0.75rem 0',
                    borderTop: '2px solid #e0e0e0',
                    marginBottom: '1rem',
                    fontSize: '1.1rem'
                  }}>
                    <strong style={{ color: 'var(--color-gray-dark)' }}>Total:</strong>
                    <strong style={{ color: 'var(--color-accent-gold)' }}>£{order.total_amount}</strong>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {order.status === 'pending' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'confirmed')}
                        style={{
                          padding: '0.75rem',
                          background: '#2196F3',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        ✓ Confirm Order
                      </button>
                    )}

                    {order.status === 'confirmed' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'preparing')}
                        style={{
                          padding: '0.75rem',
                          background: '#9c27b0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🍳 Start Preparing
                      </button>
                    )}

                    {order.status === 'preparing' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'ready')}
                        style={{
                          padding: '0.75rem',
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        ✓ Mark Ready
                      </button>
                    )}

                    {order.status === 'ready' && (
                      <button
                        onClick={() => updateOrderStatus(order.id, 'served')}
                        style={{
                          padding: '0.75rem',
                          background: 'var(--gold)',
                          color: 'black',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          cursor: 'pointer'
                        }}
                      >
                        🍽 Mark Served
                      </button>
                    )}

                    {/* Quick Message Button */}
                    <button
                      onClick={() => {
                        const message = prompt('Enter message to customer:');
                        if (message) sendNotification(order.id, message);
                      }}
                      style={{
                        padding: '0.5rem',
                        background: 'transparent',
                        color: 'var(--gold)',
                        border: '1px solid var(--gold)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      💬 Send Message
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenDashboard;
